/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// RN + NativeWind mirror of `apps/web/src/features/contacts/
// ContactsListPanel.tsx`. Middle column of the contacts route —
// search pill, All/Pending/Blocked tabs, Folders disclosure,
// Favourites strip, alphabetical contact rows. Matches Figma file
// Y95LG1oFzU2PHIvFuMYTfJ → Contacts page (541:65780), Frame
// "Contacts" (2804:85331).
//
// Conversion notes:
//   - `<aside>` -> `<View>` with `accessibilityLabel`.
//   - `<button>` -> `<Pressable>` everywhere.
//   - `<input type=search>` -> `<TextInput>` inside a styled `<View>`
//     so the rounded pill + search icon layout matches the web.
//   - `<h2>/<h3>/<span>` -> `<Text>`.
//   - `lucide-react` -> `lucide-react-native`. Icons take a `color`
//     prop because RN-side icons don't inherit `currentColor`.
//   - `transition-colors` dropped — NativeWind has no CSS-transition
//     driver. Press feedback uses Tailwind's `hover:` / `active:`.
//   - The data source is the host's `bundle` prop — the web file
//     called `useContactsBundle` directly. Keeping the v2 surface
//     presentational lets the desktop host inject AsyncStorage-backed
//     state without dragging the web Dexie path along.

import {
	ChevronDown,
	ChevronRight,
	Download,
	FolderPlus,
	type LucideIcon,
	Plus,
	Search,
	Star,
	Users,
	X,
} from "lucide-react-native";
import { memo, useCallback, useMemo, useState } from "react";
import {
	FlatList,
	type ListRenderItem,
	Pressable,
	Text,
	TextInput,
	View,
} from "react-native";
import { ContactAvatar } from "./ContactAvatar";
import type {
	ContactDirectoryUser,
	ContactsListPanelProps,
} from "./ContactsScreen.types";

const TABS = ["All", "Pending", "Blocked"] as const;
type ContactTab = (typeof TABS)[number];

export function ContactsListPanel(
	props: ContactsListPanelProps,
): JSX.Element {
	const { bundle, isLoading, selectedUserId, onUserSelect, onAddContact, onImportContacts, onCreateFolder } =
		props;

	const [query, setQuery] = useState("");
	const [tab, setTab] = useState<ContactTab>("All");
	const [foldersOpen, setFoldersOpen] = useState(true);
	const [openFolderIds, setOpenFolderIds] = useState<Set<string>>(
		() => new Set(),
	);

	const contacts = bundle.contacts;
	const folders = bundle.folders;
	const favouriteIds = bundle.favouriteIds;
	const pendingCount = bundle.pendingRequestCount;

	const contactById = useMemo(() => {
		const m = new Map<string, ContactDirectoryUser>();
		for (const c of contacts) m.set(c.id, c);
		return m;
	}, [contacts]);

	const favouriteUsers = useMemo(
		() =>
			favouriteIds
				.map((id) => contactById.get(id))
				.filter((u): u is ContactDirectoryUser => u !== undefined),
		[favouriteIds, contactById],
	);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (q.length === 0) return contacts;
		return contacts.filter(
			(u) =>
				u.displayName.toLowerCase().includes(q) ||
				u.username.toLowerCase().includes(q) ||
				(u.company !== undefined && u.company.toLowerCase().includes(q)) ||
				(u.role !== undefined && u.role.toLowerCase().includes(q)),
		);
	}, [contacts, query]);

	const grouped = useMemo(() => groupByInitial(filtered), [filtered]);

	// Stable per-panel select handler so memoized `ContactRow`s don't re-render
	// on every parent render just because a fresh inline `onPress` was created.
	const handleSelect = useCallback(
		(id: string) => onUserSelect(id),
		[onUserSelect],
	);

	// Flatten the alphabetical sections (headers + rows) into a single item
	// array so the whole directory can be windowed by a FlatList — only the
	// visible rows mount, instead of every contact in the address book.
	const listItems = useMemo<readonly ContactListItem[]>(() => {
		const items: ContactListItem[] = [];
		for (const { letter, users } of grouped) {
			items.push({ kind: "header", letter });
			for (const u of users) {
				items.push({ kind: "row", user: u });
			}
		}
		return items;
	}, [grouped]);

	const renderItem = useCallback<ListRenderItem<ContactListItem>>(
		({ item }) => {
			if (item.kind === "header") {
				return (
					<Text
						className="
							px-lg pt-md pb-xs
							font-sans text-[11px] font-semibold uppercase tracking-wider
							text-light-text-tertiary dark:text-white/40
						"
					>
						{item.letter}
					</Text>
				);
			}
			return (
				<View className="px-sm">
					<ContactRow
						user={item.user}
						selected={item.user.id === selectedUserId}
						onSelect={handleSelect}
					/>
				</View>
			);
		},
		[selectedUserId, handleSelect],
	);

	const keyExtractor = useCallback(
		(item: ContactListItem) =>
			item.kind === "header" ? `header-${item.letter}` : item.user.id,
		[],
	);

	return (
		<View
			testID="contacts-panel"
			accessibilityLabel="Contacts"
			className="
				flex flex-col w-[340px] shrink-0 h-screen min-h-0
				bg-light-surface-secondary border-r border-light-border-secondary
				dark:bg-[#111318] dark:border-white/[0.04]
			"
		>
			<PanelHeader
				title="Contacts"
				onAddContact={onAddContact}
				{...(onImportContacts !== undefined ? { onImportContacts } : {})}
			/>

			<View testID="contacts-search" className="px-lg pb-sm">
				<SearchInput
					value={query}
					onChange={setQuery}
					placeholder="Search by name, company or title…"
				/>
			</View>

			<View
				testID="contacts-filters"
				accessibilityLabel="Contact filter"
				className="
					flex flex-row items-center gap-xs px-lg pb-sm
					border-b border-light-border-secondary dark:border-white/[0.04]
				"
			>
				{TABS.map((t) => {
					const active = t === tab;
					const showBadge = t === "Pending" && pendingCount > 0;
					return (
						<Pressable
							key={t}
							accessibilityRole="button"
							onPress={() => setTab(t)}
							className={`
								flex flex-row items-center gap-xs
								px-md py-[6px] rounded-pill
								${
									active
										? "bg-light-accent-sky/15"
										: "hover:bg-light-surface-tertiary dark:hover:bg-white/[0.04]"
								}
							`}
						>
							<Text
								className={`
									font-sans text-footnote
									${
										active
											? "text-light-accent-sky"
											: "text-light-text-secondary dark:text-white/55"
									}
								`}
							>
								{t}
							</Text>
							{showBadge && (
								<View
									className="
										flex items-center justify-center
										min-w-[18px] h-[18px] px-[5px] rounded-pill
										bg-light-accent-rose
									"
								>
									<Text className="font-sans text-[10px] font-semibold leading-none text-white">
										{String(pendingCount)}
									</Text>
								</View>
							)}
						</Pressable>
					);
				})}
			</View>

			<FlatList<ContactListItem>
				className="flex-1 min-h-0"
				contentContainerClassName="pb-xxl"
				data={isLoading || tab !== "All" ? [] : listItems}
				keyExtractor={keyExtractor}
				renderItem={renderItem}
				ListHeaderComponent={
					tab !== "All" ? null : (
					<View>
						{/* Folders disclosure */}
						<DisclosureRow
							icon={Users}
							label="Folders"
							open={foldersOpen}
							onToggle={() => setFoldersOpen((v) => !v)}
							rightAffordance={
								<Pressable
									accessibilityRole="button"
									accessibilityLabel="Create folder"
									onPress={onCreateFolder ?? onAddContact}
									className="
										w-6 h-6 rounded-md flex items-center justify-center
										hover:bg-light-surface-tertiary dark:hover:bg-white/[0.06]
									"
								>
									<FolderPlus size={14} strokeWidth={1.75} color="#888d96" />
								</Pressable>
							}
						/>
						{foldersOpen && folders.length > 0 && (
							<View className="flex flex-col px-md pb-sm">
								{folders.map((folder) => {
									const open = openFolderIds.has(folder.id);
									const folderUsers = folder.contactIds
										.map((id) => contactById.get(id))
										.filter(
											(u): u is ContactDirectoryUser => u !== undefined,
										);
									return (
										<View key={folder.id}>
											<FolderRow
												name={folder.name}
												count={folderUsers.length}
												open={open}
												onToggle={() =>
													setOpenFolderIds((s) => {
														const next = new Set(s);
														if (open) next.delete(folder.id);
														else next.add(folder.id);
														return next;
													})
												}
											/>
											{open && (
												<View className="flex flex-col pl-xl pr-sm gap-[2px] py-xs">
													{folderUsers.map((u) => (
														<ContactRow
															key={`${folder.id}-${u.id}`}
															user={u}
															selected={u.id === selectedUserId}
															onSelect={handleSelect}
															compact
														/>
													))}
												</View>
											)}
										</View>
									);
								})}
							</View>
						)}

						{/* Favourites */}
						{favouriteUsers.length > 0 && (
							<>
								<DisclosureRow icon={Star} label="Favourites" open={true} />
								<View className="flex flex-col px-sm pb-sm">
									{favouriteUsers.map((u) => (
										<ContactRow
											key={`fav-${u.id}`}
											user={u}
											selected={u.id === selectedUserId}
											onSelect={handleSelect}
										/>
									))}
								</View>
							</>
						)}
					</View>
					)
				}
				ListEmptyComponent={
					isLoading ? (
						<SkeletonRows />
					) : tab !== "All" ? (
						<EmptyTabMessage tab={tab} />
					) : null
				}
				windowSize={10}
				initialNumToRender={20}
				maxToRenderPerBatch={20}
			/>
		</View>
	);
}

interface PanelHeaderProps {
	readonly title: string;
	readonly onAddContact: () => void;
	readonly onImportContacts?: (() => void) | undefined;
}

// Local panel header — equivalent to apps/web's `PanelHeader` +
// `IconChipButton` pair, inlined here so the v2 surface has no
// dependency on apps/web's `shell/ui/`.
function PanelHeader({ title, onAddContact, onImportContacts }: PanelHeaderProps): JSX.Element {
	return (
		<View
			accessibilityRole="header"
			className="flex flex-col gap-xs px-lg pt-lg pb-sm"
		>
			<View className="flex flex-row items-center justify-between">
				<Text className="text-light-text-primary dark:text-white font-sans font-bold text-[22px] leading-[1.2]">
					{title}
				</Text>
				<View className="flex flex-row items-center gap-xs">
					{onImportContacts !== undefined ? (
						<IconChipButton
							icon={Download}
							label="Import device contacts"
							onPress={onImportContacts}
						/>
					) : null}
					<IconChipButton
						icon={Plus}
						label="Add contact"
						testID="contacts-add"
						onPress={onAddContact}
					/>
				</View>
			</View>
		</View>
	);
}

interface IconChipButtonProps {
	readonly icon: LucideIcon;
	readonly label: string;
	readonly onPress: () => void;
	// Stable anchor for product tours / e2e. Maps to `data-testid` on web.
	readonly testID?: string;
}

function IconChipButton({
	icon: Icon,
	label,
	onPress,
	testID,
}: IconChipButtonProps): JSX.Element {
	return (
		<Pressable
			{...(testID !== undefined ? { testID } : {})}
			accessibilityRole="button"
			accessibilityLabel={label}
			onPress={onPress}
			className="
				flex items-center justify-center w-9 h-9 rounded-lg
				hover:bg-light-surface-tertiary dark:hover:bg-white/[0.06]
			"
		>
			<Icon size={18} strokeWidth={1.75} color="#475467" />
		</Pressable>
	);
}

interface SearchInputProps {
	readonly value: string;
	readonly onChange: (next: string) => void;
	readonly placeholder?: string;
}

// Local search input — equivalent to apps/web/shell/ui/SearchInput.
function SearchInput({
	value,
	onChange,
	placeholder = "Search...",
}: SearchInputProps): JSX.Element {
	return (
		<View
			className="
				relative flex flex-row items-center w-full h-10 rounded-pill
				bg-light-surface-secondary border border-light-border-secondary
				dark:bg-white/[0.05] dark:border-white/[0.06]
				focus-within:border-light-accent-sky
			"
		>
			<View className="ml-md">
				<Search size={16} strokeWidth={1.75} color="#888d96" />
			</View>
			<TextInput
				value={value}
				onChangeText={onChange}
				placeholder={placeholder}
				className="
					flex-1 min-w-0 px-sm py-0
					font-sans text-footnote
					text-light-text-primary dark:text-white
					placeholder:text-light-text-tertiary dark:placeholder:text-white/40
				"
			/>
			{value.length > 0 && (
				<Pressable
					accessibilityRole="button"
					accessibilityLabel="Clear search"
					onPress={() => onChange("")}
					className="
						mr-sm w-6 h-6 flex items-center justify-center rounded-full
						hover:bg-light-surface-tertiary dark:hover:bg-white/[0.06]
					"
				>
					<X size={14} strokeWidth={2} color="#888d96" />
				</Pressable>
			)}
		</View>
	);
}

interface DisclosureRowProps {
	readonly icon: LucideIcon;
	readonly label: string;
	readonly open: boolean;
	readonly onToggle?: (() => void) | undefined;
	readonly rightAffordance?: React.ReactNode;
}

function DisclosureRow({
	icon: Icon,
	label,
	open,
	onToggle,
	rightAffordance,
}: DisclosureRowProps): JSX.Element {
	const Chevron = open ? ChevronDown : ChevronRight;
	const disabled = onToggle === undefined;
	return (
		<View className="flex flex-row items-center px-lg py-sm gap-sm">
			<Pressable
				accessibilityRole="button"
				onPress={onToggle}
				disabled={disabled}
				className="flex-1 flex flex-row items-center gap-xs"
			>
				<Chevron size={12} strokeWidth={2} color="#888d96" />
				<Icon size={14} strokeWidth={1.75} color="#888d96" />
				<Text className="font-sans text-footnote font-semibold text-light-text-secondary dark:text-white/70">
					{label}
				</Text>
			</Pressable>
			{rightAffordance}
		</View>
	);
}

interface FolderRowProps {
	readonly name: string;
	readonly count: number;
	readonly open: boolean;
	readonly onToggle: () => void;
}

function FolderRow({
	name,
	count,
	open,
	onToggle,
}: FolderRowProps): JSX.Element {
	const Chevron = open ? ChevronDown : ChevronRight;
	return (
		<Pressable
			accessibilityRole="button"
			onPress={onToggle}
			className="
				w-full flex flex-row items-center gap-xs
				px-sm py-[6px] rounded-md
				hover:bg-light-surface-tertiary dark:hover:bg-white/[0.04]
			"
		>
			<Chevron size={12} strokeWidth={2} color="#888d96" />
			<Text
				numberOfLines={1}
				className="flex-1 font-sans text-footnote text-light-text-primary dark:text-white"
			>
				{name}
			</Text>
			<Text className="font-sans text-caption text-light-text-tertiary dark:text-white/55 shrink-0">
				{String(count)}
			</Text>
		</Pressable>
	);
}

interface ContactRowProps {
	readonly user: ContactDirectoryUser;
	readonly selected: boolean;
	readonly onSelect: (userId: string) => void;
	readonly compact?: boolean;
}

// Memoized so that, in the windowed directory, only rows whose props actually
// change re-render (rather than every mounted row on each parent render).
const ContactRow = memo(function ContactRow({
	user,
	selected,
	onSelect,
	compact = false,
}: ContactRowProps): JSX.Element {
	const showSubtitle =
		!compact && (user.role !== undefined || user.company !== undefined);
	return (
		<Pressable
			accessibilityRole="button"
			accessibilityState={{ selected }}
			onPress={() => onSelect(user.id)}
			className={`
				w-full flex flex-row items-center gap-md
				${compact ? "py-[5px] px-sm" : "py-sm px-md"}
				rounded-lg
				${
					selected
						? "bg-light-accent-sky/15"
						: "hover:bg-light-surface-tertiary dark:hover:bg-white/[0.04]"
				}
			`}
		>
			<ContactAvatar
				name={user.displayName}
				imageUri={user.avatarUri}
				size={compact ? "xs" : "sm"}
				presence={user.presence ?? null}
			/>
			<View className="flex-1 min-w-0 flex flex-col">
				<Text
					numberOfLines={1}
					className={`
						font-sans font-semibold
						${compact ? "text-caption" : "text-footnote"}
						${
							selected
								? "text-light-accent-sky"
								: "text-light-text-primary dark:text-white"
						}
					`}
				>
					{user.displayName}
				</Text>
				{showSubtitle && (
					<Text
						numberOfLines={1}
						className="font-sans text-caption text-light-text-secondary dark:text-white/55"
					>
						{joinNonEmpty([user.role, user.company], ", ")}
					</Text>
				)}
			</View>
		</Pressable>
	);
});

// Pending / Blocked tabs have no backing data yet (the contact-list backend
// isn't deployed) — show an honest empty state rather than the full All list.
function EmptyTabMessage({ tab }: { tab: ContactTab }): JSX.Element {
	const message =
		tab === "Pending" ? "No pending requests." : "No blocked contacts.";
	return (
		<View className="flex flex-col items-center px-lg py-xxl">
			<Text className="font-sans text-footnote text-light-text-tertiary dark:text-white/40 text-center">
				{message}
			</Text>
		</View>
	);
}

function SkeletonRows(): JSX.Element {
	return (
		<View className="flex flex-col px-sm py-md gap-xs">
			{Array.from({ length: 6 }).map((_, i) => (
				<View
					// biome-ignore lint/suspicious/noArrayIndexKey: skeleton row has no semantic key
					key={i}
					className="flex flex-row items-center gap-md p-sm"
				>
					<View className="w-8 h-8 rounded-full bg-light-surface-tertiary dark:bg-white/[0.06]" />
					<View className="flex-1 flex flex-col gap-xs">
						<View className="h-3 w-2/5 rounded bg-light-surface-tertiary dark:bg-white/[0.06]" />
						<View className="h-3 w-3/5 rounded bg-light-surface-tertiary dark:bg-white/[0.06]" />
					</View>
				</View>
			))}
		</View>
	);
}

interface InitialGroup {
	readonly letter: string;
	readonly users: readonly ContactDirectoryUser[];
}

// A single windowable row in the flattened alphabetical directory: either an
// alphabetical section header or an individual contact row.
type ContactListItem =
	| { readonly kind: "header"; readonly letter: string }
	| { readonly kind: "row"; readonly user: ContactDirectoryUser };

function groupByInitial(
	users: readonly ContactDirectoryUser[],
): readonly InitialGroup[] {
	const buckets = new Map<string, ContactDirectoryUser[]>();
	for (const u of [...users].sort((a, b) =>
		a.displayName.localeCompare(b.displayName),
	)) {
		const letter = u.displayName.charAt(0).toUpperCase();
		const list = buckets.get(letter) ?? [];
		list.push(u);
		buckets.set(letter, list);
	}
	return Array.from(buckets.entries())
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([letter, list]) => ({ letter, users: list }));
}

function joinNonEmpty(
	parts: readonly (string | undefined)[],
	sep: string,
): string {
	return parts
		.filter((p): p is string => p !== undefined && p.length > 0)
		.join(sep);
}

